'use client'

import portalPhoneImage from '@/images/auth/portal-phone.jpg'
import statusBackgroundImage from '@/images/auth/status-background.jpg'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faXmark } from '@fortawesome/pro-light-svg-icons'
import { motion, useReducedMotion } from 'framer-motion'
import Image from 'next/image'
import type { ReactNode } from 'react'

const authEntranceTransition = {
  duration: 0.92,
  ease: softEditorialEase,
} as const

type AuthSplitLayoutProps = {
  children: ReactNode
  illustrationTitle: string
  illustrationBody: string
}

export function AuthSplitLayout({
  children,
  illustrationTitle,
  illustrationBody,
}: AuthSplitLayoutProps) {
  const reduceMotion = useReducedMotion()

  return (
    <main className="flex min-h-[calc(100dvh-67px)] w-full bg-white font-sans lg:min-h-[calc(100dvh-87px)] lg:items-center">
      <motion.section
        initial={reduceMotion ? false : { opacity: 0.18, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : { ...authEntranceTransition, delay: 0.04 }
        }
        className="w-full px-5 pb-9 pt-7 lg:ml-[12.5vw] lg:w-[400px] lg:shrink-0 lg:px-0 lg:py-0"
      >
        {children}
      </motion.section>

      <motion.aside
        initial={reduceMotion ? false : { opacity: 0.15, scale: 0.99 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : {
                ...authEntranceTransition,
                duration: softMotionDuration.revealEmphasis,
                delay: 0.1,
              }
        }
        className="relative ml-auto mr-6 hidden h-[calc(100dvh-135px)] max-h-[725px] min-h-[560px] w-[45.555vw] max-w-[656px] shrink-0 overflow-hidden rounded-[28px] bg-[#0b0b0f] lg:block"
      >
        <Image
          src={portalPhoneImage}
          alt="A person using the NextClinic patient portal on their phone"
          fill
          priority
          sizes="(min-width: 1440px) 656px, 46vw"
          className="object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-[300px] bg-gradient-to-b from-transparent via-[#0b0b0f]/55 to-[#0b0b0f]/85" />

        <div className="absolute left-12 top-16 flex items-center gap-[9px] rounded-full border border-white/15 bg-white/[0.08] px-[18px] py-2.5">
          <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#8fe0c0]" />
          <span className="text-[13.5px] font-semibold leading-[18px] text-white/90">
            Most consults within the hour
          </span>
        </div>

        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { ...authEntranceTransition, duration: 0.9, delay: 0.26 }
          }
          className="absolute bottom-[96px] left-12 flex w-[540px] flex-col gap-2"
        >
          <h2 className="font-jakarta text-[32px] font-bold leading-10 tracking-[-0.01em] text-white">
            {illustrationTitle}
          </h2>
          <p className="text-[14.5px] leading-5 text-white/85">
            {illustrationBody}
          </p>
        </motion.div>
      </motion.aside>
    </main>
  )
}

type AuthStatusLayoutProps = {
  title: string
  body: ReactNode
  action?: ReactNode
  tone?: 'success' | 'error'
}

export function AuthStatusLayout({
  title,
  body,
  action,
  tone = 'success',
}: AuthStatusLayoutProps) {
  const isSuccess = tone === 'success'
  const reduceMotion = useReducedMotion()

  return (
    <main className="relative flex min-h-[calc(100dvh-67px)] w-full items-center justify-center overflow-hidden bg-white px-5 font-sans lg:min-h-[calc(100dvh-87px)] lg:px-0">
      <Image
        src={statusBackgroundImage}
        alt=""
        fill
        priority
        sizes="100vw"
        className="hidden object-cover lg:block"
      />

      <motion.section
        initial={reduceMotion ? false : { opacity: 0.18, scale: 0.99, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : { ...authEntranceTransition, delay: 0.05 }
        }
        className="relative z-10 flex w-full max-w-[335px] flex-col items-center gap-5 lg:max-w-[480px] lg:items-start lg:gap-3.5 lg:rounded-3xl lg:border lg:border-[#ececef]/80 lg:bg-white lg:p-10 lg:shadow-[0_24px_64px_rgba(11,11,15,0.08)]"
      >
        <motion.span
          initial={reduceMotion ? false : { opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { ...authEntranceTransition, duration: 0.82, delay: 0.18 }
          }
          className={`flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full ${isSuccess ? 'bg-[#dff2ec] text-[#1f8a70]' : 'bg-[#fae9e7] text-[#a2382e]'}`}
        >
          <FontAwesomeIcon
            icon={isSuccess ? faCheck : faXmark}
            aria-hidden="true"
            className="h-[22px] w-[22px]"
          />
        </motion.span>

        <div className="flex w-[279px] flex-col items-center gap-2 text-center lg:w-[400px] lg:items-start lg:text-left">
          <h1 className="text-xl font-bold leading-[26px] text-[#0b0b0f] lg:text-[26px] lg:leading-[33px] lg:tracking-[-0.01em]">
            {title}
          </h1>
          <div className="text-[13.5px] leading-[19px] text-[#82828b] lg:text-[15px] lg:leading-[21px]">
            {body}
          </div>
        </div>

        {action && <div className="lg:mt-1">{action}</div>}
      </motion.section>
    </main>
  )
}
